import {
  Button,
  Diagnostic,
  Progress,
  Status,
  Subtitle,
  SystemScreen,
} from "@fwf/fwfa-design-system/react";
import type { AuthChallenge, ConnectionStatus, InstallProgress } from "@/types";

interface Props {
  mode: "starting" | "reconnecting";
  progress: number;
  headline: string;
  detail: string;
  status: ConnectionStatus;
  installProgress: InstallProgress | null;
  authChallenge?: AuthChallenge | null;
  error?: string | null;
  errorDetails?: string | null;
  onRetry?: () => void;
  onChooseWorkspace: () => void;
  choosingWorkspace?: boolean;
}

export function WorkspaceBootScreen({
  mode,
  progress,
  headline,
  detail,
  status,
  installProgress,
  authChallenge,
  error,
  errorDetails,
  onRetry,
  onChooseWorkspace,
  choosingWorkspace,
}: Props) {
  const modeLabel = mode === "starting" ? "Starting workspace" : "Reconnecting workspace";
  const statusLabel = error ? "Error" : status.replace(/:/g, " ");

  const chooseWorkspaceButton = (
    <Button variant="secondary" onClick={onChooseWorkspace} disabled={choosingWorkspace}>
      {choosingWorkspace ? "Opening…" : "Choose different workspace"}
    </Button>
  );

  return (
    <div className="relative h-full animate-fade-in">
      <div className="drag-region absolute inset-x-0 top-0 z-10 h-12" />
      <SystemScreen
        className="h-full min-h-0 overflow-y-auto"
        tone={error ? "danger" : undefined}
        kicker={modeLabel}
        title={headline}
        actions={
          error ? (
            <>
              {onRetry && (
                <Button variant="primary" onClick={onRetry}>
                  Retry connection
                </Button>
              )}
              {chooseWorkspaceButton}
            </>
          ) : (
            chooseWorkspaceButton
          )
        }
      >
        <Subtitle>{detail}</Subtitle>

        <div>
          <Progress
            value={progress}
            tone={error ? "danger" : undefined}
            label={modeLabel}
          />
          <div className="mt-2 flex items-center justify-between gap-2 text-sm text-ink-soft">
            <span>{Math.round(progress)}% ready</span>
            {error ? (
              <Status tone="danger" alert>
                {statusLabel}
              </Status>
            ) : (
              <Status live className="capitalize">
                {statusLabel}
              </Status>
            )}
          </div>
        </div>

        {installProgress?.message && (
          <p className="text-sm text-ink-soft">{installProgress.message}</p>
        )}

        {!error && authChallenge && (
          <div className="space-y-2">
            <p className="text-sm leading-relaxed text-ink">
              Authentication is required for {authChallenge.agentName}.
            </p>
            {authChallenge.terminalCommand && (
              <Diagnostic>{authChallenge.terminalCommand}</Diagnostic>
            )}
            {authChallenge.url && (
              <p className="break-all text-xs text-ink-soft">{authChallenge.url}</p>
            )}
          </div>
        )}

        {error && (
          <div className="space-y-2">
            <p className="text-sm leading-relaxed text-ink">{error}</p>
            {errorDetails && errorDetails !== error && (
              <Diagnostic summary="Error details">{errorDetails}</Diagnostic>
            )}
          </div>
        )}
      </SystemScreen>
    </div>
  );
}
